import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  assembleKitRequest,
  disassembleKitRequest,
  getKitPlanRequest,
  getKitRequest,
  getKitsRequest,
} from './operatorKitsApi';
import { kitSourcesShown } from './operatorKitsActions';
import {
  KIT_WORK,
  KIT_STEP,
  buildAssembleBody,
  buildDisassembleBody,
  isKitBusy,
  isKitWriteArg,
  kitOption,
  kitQuantityProblem,
  kitWriteArgOf,
  matchKits,
  planArgOf,
  suggestedKitDestination,
} from './operatorKitsUtils';
import { destinationProblem } from '../operatorPutaway/operatorPutawayUtils';
import { scanCodeRequest } from '../operator/operatorApi';
import { OPERATOR_FLOW } from '../operator/operatorConstants';
import { OPERATOR_ERROR, localError, readOperatorError } from '../operator/operatorErrors';
import { idempotentThunkOptions, sameAction, sendWrite } from '../operator/operatorIdempotency';
import { SCAN_GATE, cleanCode, scanGate } from '../operator/operatorScanGate';
import { offScreenMeta } from '../operator/operatorTaskScope';
import { selectCurrentOperatorWarehouseId } from '../operator/operatorUtils';

const warehouseOf = (getState) => selectCurrentOperatorWarehouseId(getState());

const kitsOf = (getState) => getState().operatorKits;

/** The station's kits, a page at a time. Page 1 replaces, the rest append. */
export const fetchKits = createAsyncThunk(
  'operatorKits/fetchKits',
  async ({ page = 1 } = {}, { getState, rejectWithValue }) => {
    try {
      const { data } = await getKitsRequest(warehouseOf(getState), { page });
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos cargar los kits.'));
    }
  }
);

/** @param {number} kitId */
export const fetchKit = createAsyncThunk('operatorKits/fetchKit', async (kitId, { getState, rejectWithValue }) => {
  try {
    const { data } = await getKitRequest(warehouseOf(getState), kitId);
    return data;
  } catch (error) {
    return rejectWithValue(readOperatorError(error, 'No pudimos abrir el kit.'));
  }
});

/** @param {{kitId: number, mode: string, quantity: number}} arg */
export const fetchKitPlan = createAsyncThunk(
  'operatorKits/fetchPlan',
  async ({ kitId, mode, quantity }, { getState, rejectWithValue }) => {
    try {
      const { data } = await getKitPlanRequest(warehouseOf(getState), kitId, { mode, quantity });
      return data;
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos calcular de dónde sale.'));
    }
  },
  { getPendingMeta: ({ arg }) => ({ pendingKey: arg.kitId }) }
);

/**
 * The kit a code read at the station is: among the kits listed first, as the
 * server matches them (barcode, SKU, a pack's barcode); otherwise the server
 * is asked. One kit opens; several are offered to choose from. Once the
 * station is off screen the answer opens nothing (`meta.offScreenTask`).
 *
 * @param {{code: string, source: string}} read
 */
export const findKit = createAsyncThunk(
  'operatorKits/findKit',
  async ({ code }, { getState, fulfillWithValue, rejectWithValue }) => {
    const value = cleanCode(code);
    const stationMeta = () => offScreenMeta(getState(), OPERATOR_FLOW.KITS, null, value);
    const found = (rows) =>
      rows.length === 1 ? { kind: 'kit', kit: rows[0] } : { kind: 'choose', code: value, options: rows.map(kitOption) };

    const listed = matchKits(kitsOf(getState).list.items, value);
    if (listed.length > 0) return fulfillWithValue(found(listed), stationMeta());
    try {
      const { data } = await getKitsRequest(warehouseOf(getState), { code: value });
      const rows = data?.results || [];
      if (rows.length === 0) return rejectWithValue(localError(`${value} no es un kit de esta bodega.`), stationMeta());
      return fulfillWithValue(found(rows), stationMeta());
    } catch (error) {
      return rejectWithValue(readOperatorError(error, 'No pudimos buscar ese kit.'), stationMeta());
    }
  },
  {
    condition: (read, { getState }) => {
      const { station } = kitsOf(getState);
      return scanGate({ busy: station.finding, lastCode: station.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * A code read where the kits — or, taking them apart, their components — are
 * left. A slot the plan suggests is known already; any other is asked to the
 * scan, so a code that is not a slot, or one where stock cannot go, is said
 * right away.
 *
 * @param {{code: string, source: string}} read
 */
export const scanKitCode = createAsyncThunk(
  'operatorKits/scanCode',
  async ({ code }, { getState, fulfillWithValue, rejectWithValue }) => {
    const { kitId, kit, plan } = kitsOf(getState);
    const value = cleanCode(code);
    const meta = () => offScreenMeta(getState(), OPERATOR_FLOW.KITS, kitId, kit?.sku);

    const suggested = suggestedKitDestination(plan, value);
    if (suggested) return fulfillWithValue({ kitId, destination: suggested }, meta());
    try {
      const { data } = await scanCodeRequest(warehouseOf(getState), value, ['slot']);
      const slot = (data.results || []).find((result) => result.kind === 'slot');
      if (!slot) return rejectWithValue(localError(`${value} no es una ubicación.`), meta());
      const problem = destinationProblem(slot);
      if (problem) return rejectWithValue(localError(problem), meta());
      return fulfillWithValue({ kitId, destination: { code: slot.code, name: slot.name || slot.code, suggested: false } }, meta());
    } catch (error) {
      const failure = readOperatorError(error, 'No pudimos leer esa ubicación.');
      return rejectWithValue(
        failure.kind === OPERATOR_ERROR.NOT_FOUND ? localError(`${value} no es una ubicación de esta bodega.`) : failure,
        meta()
      );
    }
  },
  {
    condition: (read, { getState }) => {
      const visit = kitsOf(getState);
      const busy = visit.step !== KIT_STEP.DESTINATION || isKitBusy(visit);
      return scanGate({ busy, lastCode: visit.scan.lastCode }, read) === SCAN_GATE.ACCEPT;
    },
  }
);

/**
 * A kit opened: it is asked again, and so is its plan when the visit is on
 * its sources without one — a 412 that answered while the kit was off screen.
 *
 * @param {number} kitId
 */
export const loadKit = (kitId) => (dispatch, getState) => {
  const visit = kitsOf(getState);
  if (visit.kitId === kitId && visit.step === KIT_STEP.SOURCES && !visit.plan && !visit.planLoading) {
    dispatch(fetchKitPlan(planArgOf(visit)));
  }
  return dispatch(fetchKit(kitId));
};

/** «Ver de dónde sale»: the visit goes to its sources, and the plan for the quantity on screen is asked. */
export const showKitSources = () => (dispatch, getState) => {
  const visit = kitsOf(getState);
  if (!visit.kit || visit.step !== KIT_STEP.QUANTITY || isKitBusy(visit)) return null;
  if (kitQuantityProblem(visit.quantity, visit.kit, visit.mode)) return null;
  dispatch(kitSourcesShown());
  return dispatch(fetchKitPlan(planArgOf(visit)));
};

/** The plan of the visit asked again: after it failed, or once the stock changed under it. */
export const reloadKitPlan = () => (dispatch, getState) => dispatch(fetchKitPlan(planArgOf(kitsOf(getState))));

/** A 412 says why, and up to how many can be done now (`maxQuantity`). */
const readKitError = (error, fallback) => {
  const failure = readOperatorError(error, fallback);
  if (failure.kind !== OPERATOR_ERROR.STALE) return failure;
  const body = error?.response?.data || {};
  return {
    ...failure,
    reason: body.reason ?? null,
    maxQuantity: body.max_quantity != null ? Number(body.max_quantity) : null,
  };
};

/**
 * Where a write's answer lands, read when it arrives. A kit off screen gets
 * the band named after it (`meta.offScreenTask`). The kit on screen takes the
 * answer only if it is the write its visit waits for; any other was sent on
 * an earlier visit (`meta.earlierVisit`), and the kit is loaded again instead.
 */
const answerMeta = ({ getState, requestId }, arg) => {
  const state = getState();
  const meta = offScreenMeta(state, OPERATOR_FLOW.KITS, arg.kitId, arg.kitSku);
  if (meta) return meta;
  return state.operatorKits.lastWrite?.key === requestId ? undefined : { earlierVisit: true };
};

const writeOptions = {
  ...idempotentThunkOptions,
  condition: (arg, thunkApi) => {
    const visit = kitsOf(thunkApi.getState);
    if (!isKitWriteArg(arg)) return false;
    // One write of the visit at a time: another is a new action, not this one again.
    if (arg.kitId === visit.kitId && visit.pending) return false;
    return idempotentThunkOptions.condition(arg, thunkApi);
  },
  // Keyed by kit, so a write left out on one does not keep another's buttons busy.
  getPendingMeta: ({ arg }) => ({ pendingKey: arg?.kitId }),
};

/**
 * Puts kits together: the components from where the plan — and the operator
 * — said, the kits into the slot scanned. The key is the thunk's request id.
 *
 * @param {{kitId: number, kitSku: string, quantity: number, sources: Array<{productId: number,
 *   slotCode: string, lot: string, quantity: number}>, toSlotCode: string, lot?: string,
 *   expiryDate?: string, idempotencyKey?: string}} arg
 */
export const assembleKit = createAsyncThunk(
  'operatorKits/assemble',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await assembleKitRequest(warehouseOf(getState), arg.kitId, buildAssembleBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg));
    } catch (error) {
      return rejectWithValue(readKitError(error, 'No pudimos armar los kits.'), answerMeta(thunkApi, arg));
    }
  },
  writeOptions
);

/**
 * Takes kits apart: out of one slot and lot, their components into the slot
 * scanned.
 *
 * @param {{kitId: number, kitSku: string, quantity: number, fromSlotCode: string, lot: string,
 *   toSlotCode: string, idempotencyKey?: string}} arg
 */
export const disassembleKit = createAsyncThunk(
  'operatorKits/disassemble',
  async (arg, thunkApi) => {
    const { getState, requestId, fulfillWithValue, rejectWithValue } = thunkApi;
    try {
      const { data } = await disassembleKitRequest(warehouseOf(getState), arg.kitId, buildDisassembleBody(arg, requestId));
      return fulfillWithValue(data, answerMeta(thunkApi, arg));
    } catch (error) {
      return rejectWithValue(readKitError(error, 'No pudimos desarmar los kits.'), answerMeta(thunkApi, arg));
    }
  },
  writeOptions
);

/** The station's writes, by type: what its band and its own «Reintentar» send again. */
export const KIT_WRITES = {
  [assembleKit.typePrefix]: assembleKit,
  [disassembleKit.typePrefix]: disassembleKit,
};

/**
 * The confirmation's primary. The same write sent again while the answer to
 * its last send is lost goes under the key it was first sent with, so the
 * server registers it once.
 */
export const submitKitWrite = () => (dispatch, getState) => {
  const visit = kitsOf(getState);
  const arg = kitWriteArgOf(visit);
  if (!arg) return null;
  const thunk = visit.mode === KIT_WORK.DISASSEMBLE ? disassembleKit : assembleKit;
  return dispatch(sendWrite(thunk, arg, (state) => state.operatorKits));
};

/** The visit's own «Reintentar»: the write whose answer was lost, under the same key. */
export const retryLastKitWrite = () => (dispatch, getState) => {
  const { lastWrite, pending } = kitsOf(getState);
  const thunk = lastWrite && !pending ? KIT_WRITES[lastWrite.type] : null;
  return thunk ? dispatch(thunk(sameAction(lastWrite))) : null;
};
